/**
 * THE ATTACK ARROW - attacker to target, the original's `ManualArrowSystem`.
 *
 * ⭐ Alex, 2026-08-14: *"there should be an animation of arrows from the attack card to the shield
 * or the card it attacks."* It is not decoration: in a select-then-target game the arrow is the only
 * thing that says WHICH of your creatures is hitting WHAT, at the moment it matters.
 *
 * 🚨 **IT IS AN OVERLAY ON THE STAGE, NOT A CHILD OF EITHER CARD, AND THAT IS THE WHOLE DESIGN.**
 * Both combatants can die in the same server batch, and the board calls `replaceChildren()` on every
 * event - so anything parented to a card is destroyed mid-flight. That is precisely how the
 * attacker's lunge kept vanishing and getting reported as "the attack animations are not working".
 * The arrow outlives its endpoints by construction: it reads their positions ONCE, at fire time, and
 * owns nothing afterwards.
 *
 * ⚠️ **POSITIONS ARE READ IN STAGE SPACE, NOT SCREEN SPACE.** The stage is `transform: scale()`d to
 * the window, so a `getBoundingClientRect()` is in scaled pixels while the SVG draws in unscaled
 * stage units. Dividing by the live scale is what keeps the arrow on the cards at every window size
 * rather than only at 1:1 - the same trap the legibility probe documents.
 */

const ARROW_LIFE_MS = 560;

/** Fire an arrow from one element to another. Both may be destroyed immediately afterwards. */
export function fireArrow(stage: HTMLElement, from: Element, to: Element): void {
  const host = stage.getBoundingClientRect();
  // The stage's own scale, so client pixels convert back to stage units.
  const scale =
    Number(getComputedStyle(stage).getPropertyValue("--stage-scale")) || 1;
  if (host.width <= 0 || scale <= 0) return;

  const a = from.getBoundingClientRect();
  const b = to.getBoundingClientRect();
  const pt = (r: DOMRect) => ({
    x: (r.left + r.width / 2 - host.left) / scale,
    y: (r.top + r.height / 2 - host.top) / scale,
  });
  const p0 = pt(a);
  const p1 = pt(b);

  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 4) return;

  // ⚠️ **BOWED, NOT STRAIGHT.** A straight line between two cards in the same column overlaps the
  // cards between them and reads as a UI seam. The control point is pushed perpendicular to the
  // line, proportionally, so short and long arrows keep the same character.
  const bow = Math.min(90, dist * 0.22);
  const cx = (p0.x + p1.x) / 2 - (dy / dist) * bow;
  const cy = (p0.y + p1.y) / 2 + (dx / dist) * bow;

  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("class", "arrow-layer");
  svg.setAttribute(
    "viewBox",
    `0 0 ${Math.round(host.width / scale)} ${Math.round(host.height / scale)}`,
  );

  const path = document.createElementNS(NS, "path");
  path.setAttribute("class", "arrow-layer__path");
  path.setAttribute("d", `M ${p0.x} ${p0.y} Q ${cx} ${cy} ${p1.x} ${p1.y}`);

  const head = document.createElementNS(NS, "circle");
  head.setAttribute("class", "arrow-layer__head");
  head.setAttribute("cx", String(p1.x));
  head.setAttribute("cy", String(p1.y));
  head.setAttribute("r", "9");

  svg.append(path, head);
  stage.append(svg);

  // The dash length drives the draw-on animation and has to be the REAL path length, not the
  // straight-line distance - a bowed path is longer, and a short dash would finish drawing early.
  let len = dist;
  try {
    len = path.getTotalLength() || dist;
  } catch {
    /* getTotalLength is unavailable in some headless contexts; the chord is a close enough dash. */
  }
  path.style.setProperty("--len", String(Math.ceil(len)));

  // ⚠️ Removed on a timer, NOT on `animationend`: two animations run here and the element must go
  // even if one of them never fires (reduced-motion, a background tab throttling rAF).
  window.setTimeout(() => svg.remove(), ARROW_LIFE_MS);
}
